"use client";

import { useState } from "react";
import { CalendarPlus, Printer } from "lucide-react";

type ExportItineraryProps = {
  cityName: string;
  /** Trip start date as YYYY-MM-DD, if the visitor picked one (?start=). */
  start?: string;
};

const todayIso = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Escape a value for an iCalendar text property (RFC 5545).
const icsEscape = (text: string) =>
  text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");

// Fold lines longer than 75 octets per RFC 5545 (CRLF + single space).
const icsFold = (line: string) => {
  const chunks: string[] = [];
  let rest = line;
  while (rest.length > 73) {
    chunks.push(rest.slice(0, 73));
    rest = " " + rest.slice(73);
  }
  chunks.push(rest);
  return chunks.join("\r\n");
};

const yyyymmdd = (year: number, monthIndex: number, day: number) => {
  const d = new Date(year, monthIndex, day);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
};

export default function ExportItinerary({
  cityName,
  start,
}: ExportItineraryProps) {
  const [startDate, setStartDate] = useState(start || todayIso());

  const handlePrint = () => window.print();

  const handleCalendar = () => {
    const output = document.getElementById("itineraryOutput");
    if (!output) return;
    const sections = Array.from(output.querySelectorAll("section"));
    if (sections.length === 0) return;

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDate);
    const [baseY, baseM, baseD] = match
      ? [Number(match[1]), Number(match[2]) - 1, Number(match[3])]
      : (() => {
          const t = new Date();
          return [t.getFullYear(), t.getMonth(), t.getDate()];
        })();

    const stamp = new Date()
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d+Z$/, "Z");

    const lines: string[] = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//AI Trip Maker//Itinerary//EN",
      "CALSCALE:GREGORIAN",
    ];

    sections.forEach((section, i) => {
      const title =
        section.querySelector("h2")?.textContent?.trim() ||
        `Day ${i + 1} in ${cityName}`;
      const paragraphs = Array.from(section.querySelectorAll("p"))
        .map((p) => p.textContent?.trim() || "")
        .filter(Boolean);
      const description = paragraphs.join("\n\n");

      const dtStart = yyyymmdd(baseY, baseM, baseD + i);
      const dtEnd = yyyymmdd(baseY, baseM, baseD + i + 1);

      lines.push(
        "BEGIN:VEVENT",
        icsFold(`UID:${stamp}-day${i + 1}@aitripmaker.com`),
        `DTSTAMP:${stamp}`,
        `DTSTART;VALUE=DATE:${dtStart}`,
        `DTEND;VALUE=DATE:${dtEnd}`,
        icsFold(`SUMMARY:${icsEscape(title)}`),
        icsFold(`DESCRIPTION:${icsEscape(description)}`),
        icsFold(`LOCATION:${icsEscape(cityName)}`),
        "END:VEVENT"
      );
    });

    lines.push("END:VCALENDAR");

    const blob = new Blob([lines.join("\r\n")], {
      type: "text/calendar;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${cityName.toLowerCase().replace(/\s+/g, "-")}-itinerary.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const btn =
    "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors cursor-pointer border";

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
      <button
        type="button"
        onClick={handlePrint}
        className={`${btn} border-orange-300 text-orange-700 hover:bg-orange-50`}
      >
        <Printer className="h-4 w-4" /> Print / Save as PDF
      </button>

      <div className="flex items-center gap-2">
        <input
          type="date"
          aria-label="Trip start date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
        />
        <button
          type="button"
          onClick={handleCalendar}
          className={`${btn} border-transparent bg-gradient-to-r from-orange-500 to-amber-400 text-white shadow-md hover:from-orange-600 hover:to-amber-500`}
        >
          <CalendarPlus className="h-4 w-4" /> Add to Calendar
        </button>
      </div>
    </div>
  );
}
